/**
 * Tri chronologique décroissant des candidats à la sélection de source d'acte.
 *
 * WHY la date intrinsèque prime sur `created_at` : une écriture adverse de 2018
 * numérisée hier porte la date de 2018, pas celle du dépôt numérique. Trier sur
 * `created_at` remonterait les nouvelles numérisations en tête, faisant croire à
 * l'avocat qu'une vieille pièce est la plus récente du dossier.
 *
 * WHY une pièce sans date atterrit en bas : elle ne doit jamais prétendre être
 * récente faute d'information. Mieux vaut la reléguer que laisser l'avocat choisir
 * une écriture ancienne dont il ne peut pas connaître l'antériorité.
 */

/** Forme minimale requise pour le tri chronologique. */
export interface CandidatAvecDate {
  id: string;
  name: string;
  document_date?: string | null;
}

/**
 * Retourne une copie triée du tableau, par date intrinsèque décroissante
 * (NULLS LAST), puis par nom croissant à égalité de date.
 *
 * Ne mute jamais le tableau d'entrée.
 */
export function trierCandidatsParDate<T extends CandidatAvecDate>(candidates: T[]): T[] {
  return [...candidates].sort((a, b) => {
    const timeA = parseDateSafe(a.document_date);
    const timeB = parseDateSafe(b.document_date);

    // Pièces sans date en dernier, indépendamment du nom.
    if (timeA === null && timeB === null) return a.name.localeCompare(b.name, 'fr');
    if (timeA === null) return 1;
    if (timeB === null) return -1;

    // Date décroissante (la plus récente en premier).
    if (timeA !== timeB) return timeB - timeA;

    // Départage à égalité : nom croissant.
    return a.name.localeCompare(b.name, 'fr');
  });
}

/**
 * Parse une valeur de date brute en timestamp ms, ou retourne `null` si la
 * valeur est absente ou non parsable (valeur ISO invalide, chaîne vide…).
 */
function parseDateSafe(raw: string | null | undefined): number | null {
  if (!raw) return null;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : null;
}
